import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { categoriesAPI, providersAPI, citiesAPI } from '../services/api';
import { Star, MapPin, Filter, ChevronDown, ArrowLeft, Map as MapIcon, List } from 'lucide-react';
import Footer from '../components/Footer';
import ProvidersMap from '../components/ProvidersMap';

const CategoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedCity, setSelectedCity] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = categories.find((c) => c.id === id);
  const currentCity = cities.find((c) => c.id === selectedCity);

  useEffect(() => {
    categoriesAPI.getAll().then(res => setCategories(res.data)).catch(() => {});
    citiesAPI.getAll().then(res => setCities(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { category: id, sort: sortBy };
    if (selectedCity !== 'all') params.city = selectedCity;
    providersAPI.getAll(params)
      .then(res => setProviders(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, selectedCity, sortBy]);

  if (!category) {
    return (
      <div className="pt-24 px-4 text-center">
        <p className="text-gray-500">Catégorie introuvable</p>
        <Link to="/" className="text-blue-500 hover:underline mt-4 inline-block">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors"
          >
            <ArrowLeft size={16} />
            Retour
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{category?.name || 'Catégorie'}</h1>
          <p className="text-gray-500">{providers.length} prestataires disponibles</p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Filter size={16} className="text-gray-500" />
              <span className="text-gray-600 font-medium">Filtrer :</span>
            </div>

            {/* City filter */}
            <div className="relative">
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="appearance-none bg-white border border-gray-200 rounded-full px-4 py-2 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              >
                <option value="all">Toutes les villes</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.name}>{city.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* Sort */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none bg-white border border-gray-200 rounded-full px-4 py-2 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              >
                <option value="rating">Meilleure note</option>
                <option value="price_asc">Prix croissant</option>
                <option value="price_desc">Prix décroissant</option>
                <option value="reviews">Plus d'avis</option>
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* View mode toggle */}
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <List size={16} />
              Liste
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                viewMode === 'map'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <MapIcon size={16} />
              Carte
            </button>
          </div>
        </div>
      </div>

      {/* Provider Grid / Map */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {loading ? (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg">Chargement...</p>
          </div>
        ) : providers.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg">Aucun prestataire trouvé pour cette catégorie.</p>
          </div>
        ) : viewMode === 'map' ? (
          <ProvidersMap providers={providers} centerCity={currentCity} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {providers.map((provider) => (
              <div
                key={provider.id}
                onClick={() => navigate(`/booking/${provider.id}`)}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow group"
              >
                <div className="relative h-[200px] overflow-hidden">
                  <img
                    src={provider.photo}
                    alt={provider.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {provider.is_top && (
                    <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm text-xs font-semibold text-gray-800 px-2.5 py-1 rounded-full shadow-sm">
                      Top prestataire
                    </div>
                  )}
                  {provider.is_pro && (
                    <div className="absolute top-3 right-3 bg-blue-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                      PRO
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-base font-semibold text-gray-900">{provider.name}</h3>
                    <span className="text-sm font-bold text-gray-900">{provider.price} DH</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-1">{provider.skill}</p>
                  <div className="flex items-center gap-1 text-sm text-gray-400 mb-2">
                    <MapPin size={12} />
                    {provider.city}
                  </div>
                  <div className="flex items-center gap-1 mb-3">
                    <Star size={14} className="text-yellow-400 fill-yellow-400" />
                    <span className="text-sm font-bold text-gray-900">{provider.rating}</span>
                    <span className="text-xs text-gray-400">({provider.reviews_count || provider.reviews} avis)</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {provider.tags.slice(0, 2).map((tag, idx) => (
                      <span key={idx} className="text-[11px] font-medium text-gray-600 bg-gray-50 px-2 py-0.5 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default CategoryPage;
